import PageGlow from "../components/PageGlow";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";

const TIERS = [
  {
    id: "free", name: "Free", tagline: "Dip a toe in.",
    points: [
      "One live job at a time",
      "Listed for 7 days",
      "Applications straight to your dashboard",
      "Reviewed by our team before going live",
    ],
    featured: false, cta: "Post a free job",
  },
  {
    id: "standard", name: "Standard", tagline: "Get seen sooner.",
    points: [
      "Listed above all free jobs",
      "Live for 30 days",
      "Edit your listing any time",
      "Applications straight to your dashboard",
    ],
    featured: false, cta: "Post a standard job",
  },
  {
    id: "featured", name: "Featured", tagline: "Front of the queue.",
    points: [
      "Pinned to the top of search results",
      "Shown on the homepage",
      "Featured badge that stands out",
      "Live for 30 days, edit any time",
    ],
    featured: true, cta: "Post a featured job",
  },
];

const FAQS = [
  { q: "Why does every job get reviewed?", a: "Because jobseekers trust a board where every listing is genuine. Our team reads each listing before it goes live. It usually takes less than a working day." },
  { q: "What happens after 30 days?", a: "Your listing comes down automatically. Want it back up? Repost from your dashboard in a couple of clicks." },
  { q: "Can I get a refund?", a: "If we reject your paid listing, you're refunded in full, automatically. Once a job is live, the fee is spent, but you can edit the listing as much as you like during the 30 days." },
  { q: "Is it really free for jobseekers?", a: "Yes. Searching, applying and saving jobs costs nothing, and we don't sell your data. Employers pay, jobseekers don't." },
];

export default function Pricing() {
  const { user } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [sub, setSub] = useState(null);

  useEffect(() => {
    api.get("/pricing").then((res) => setPricing(res.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (user?.role === "employer") {
      api.get("/subscriptions/my").then((r) => setSub(r.data.subscription)).catch(() => {});
    }
  }, [user]);

  const subActive = sub && ["active", "past_due", "cancelling"].includes(sub.status);

  const priceLabel = (id) => pricing?.[id]?.price ?? (id === "standard" ? 20 : 49);
  const compareLabel = (id) => pricing?.[id]?.compare_at;

  return (
    <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20" data-testid="pricing-page">
      <PageGlow />
      {subActive && (
        <div className="relative mb-8 max-w-2xl border border-green-200 bg-green-50 rounded-md px-4 py-3 text-sm text-green-900"
          data-testid="pricing-current-coverage">
          You have an active <strong>{sub.plan_name}</strong> subscription, {sub.included_jobs} listings every month,
          {" "}{sub.monthly_remaining} of {sub.credits_total} credits left this month
          {(sub.cancel_at_period_end || sub.status === "cancelling") ? (
            <> · ends {new Date(sub.paid_until || sub.credit_period_end).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}, no further charges</>
          ) : sub.paid_until && !sub.custom ? (
            <> · renews {new Date(sub.paid_until).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} at £{Number(sub.monthly_price).toLocaleString("en-GB")}/month</>
          ) : null}.
          {" "}<Link to="/plans" className="font-semibold underline underline-offset-2 hover:text-green-950" data-testid="pricing-coverage-plans-link">See your plan options</Link>
        </div>
      )}
      <Seo
        title="UK Job Advertising Prices | HiredToday"
        description="Post a job on HiredToday: free listing, Standard and Featured options with more reach."
        path="/pricing"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": FAQS.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        }}
      />
      <div className="max-w-2xl">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">Employer pricing</p>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-black text-zinc-950">
          Honest prices, per listing.
        </h1>
        <p className="mt-5 text-base sm:text-lg text-zinc-600 leading-relaxed">
          Pay per posting, or save with a monthly plan if you hire regularly. Every listing includes a review and applications delivered straight to your dashboard.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mt-14">
        {TIERS.map((t, i) => (
          <div key={t.id} data-testid={`pricing-card-${t.id}`}
            className={`animate-fade-up rounded-md p-8 flex flex-col border ${
              t.featured ? "border-yellow-300 bg-yellow-50/50 border-t-4 border-t-yellow-400" : "border-zinc-200 bg-white"}`}
            style={{ animationDelay: `${i * 80}ms` }}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-bold text-zinc-950">{t.name}</h2>
              {t.featured && <span className="text-[11px] font-bold uppercase tracking-wider bg-yellow-400 text-zinc-950 px-2 py-0.5 rounded">Most reach</span>}
            </div>
            <p className="text-sm text-zinc-500 mt-1">{t.tagline}</p>
            <p className="font-display text-5xl font-black text-zinc-950 mt-6">
              {t.id === "free" ? "Free" : (
                <>
                  {compareLabel(t.id) && (
                    <span className="text-xl font-bold text-zinc-400 line-through mr-2">£{compareLabel(t.id)}</span>
                  )}
                  £{priceLabel(t.id)}
                </>
              )}
            </p>
            <p className="text-xs text-zinc-400 mt-1">per job, {t.id === "free" ? "7" : "30"} days live</p>
            <ul className="mt-7 space-y-3 flex-1">
              {t.points.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-sm text-zinc-700">
                  <CheckCircle2 className={`h-4 w-4 mt-0.5 shrink-0 ${t.featured ? "text-yellow-600" : "text-zinc-400"}`} />{p}
                </li>
              ))}
            </ul>
            <Link to={`/post-a-job?tier=${t.id}`} data-testid={`pricing-cta-${t.id}`}
              className={`group mt-8 inline-flex items-center justify-center gap-2 h-12 rounded-md text-sm font-semibold transition-colors duration-200 ${
                t.featured ? "bg-zinc-950 text-white hover:bg-zinc-800" : "border border-zinc-300 text-zinc-900 hover:border-zinc-500"}`}>
              {t.cta} <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            {t.id === "standard" && (
              <Link to="/plans" data-testid="pricing-view-plans"
                className="mt-3 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-900 transition-colors duration-200">
                Posting regularly? View plans <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            )}
          </div>
        ))}
      </div>

      <div className="mt-8 grid sm:grid-cols-2 gap-4 max-w-4xl">
        <div className="border border-zinc-200 rounded-md p-5 bg-white flex items-start gap-3" data-testid="pricing-urgent-addon">
          <span className="text-[11px] font-bold uppercase tracking-wider bg-red-100 text-red-700 px-2 py-0.5 rounded mt-0.5">Urgent</span>
          <p className="text-sm text-zinc-600">
            Need it filled fast? Add <span className="font-semibold text-zinc-900">Urgent for £{pricing?.urgent?.price ?? 9}</span> to any
            paid listing, red badge and priority placement for the full 30 days.
          </p>
        </div>
        <div className="border border-zinc-200 rounded-md p-5 bg-white flex items-start gap-3" data-testid="pricing-annual-discount">
          <span className="text-[11px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 px-2 py-0.5 rounded mt-0.5">Plans</span>
          <p className="text-sm text-zinc-600">
            Hiring regularly? <Link to="/plans" className="font-semibold text-blue-700 hover:text-blue-900">Monthly plans from £59</Link> —
            pay annually and save 5%.
          </p>
        </div>
      </div>

      <div className="mt-8 max-w-4xl" data-testid="pricing-assistant-prompt">
        <div className="relative overflow-hidden rounded-md border border-violet-200 bg-gradient-to-r from-violet-50 via-purple-50 to-fuchsia-50 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="h-11 w-11 shrink-0 rounded-full bg-gradient-to-br from-violet-500 via-purple-600 to-fuchsia-600 text-white inline-flex items-center justify-center shadow-md shadow-purple-500/25">
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-bold text-zinc-950">Not sure which fits?</p>
            <p className="text-sm text-zinc-600 mt-0.5">Ask the HiredToday assistant anything about hiring here, tiers, plans, reach, how review works. It will point you at the right option.</p>
          </div>
          <button onClick={() => window.dispatchEvent(new CustomEvent("ht:open-assistant"))} data-testid="pricing-ask-assistant"
            className="shrink-0 h-11 px-6 bg-gradient-to-br from-violet-600 to-purple-600 text-white text-sm font-semibold rounded-md hover:shadow-lg hover:shadow-purple-500/30 hover:scale-[1.03] active:scale-95 transition-[transform,box-shadow] duration-200">
            Ask the assistant
          </button>
        </div>
      </div>

      <div className="mt-20 max-w-3xl">
        <h2 className="font-display text-3xl tracking-tight font-bold text-zinc-950 mb-8">Fair questions</h2>
        <div className="divide-y divide-zinc-100 border-t border-b border-zinc-100">
          {FAQS.map((f) => (
            <details key={f.q} className="group py-5" data-testid={`faq-${f.q.slice(0, 20).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <summary className="cursor-pointer list-none flex items-center justify-between font-medium text-zinc-900">
                {f.q}
                <span className="text-zinc-400 group-open:rotate-45 transition-transform duration-200 text-xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-sm text-zinc-600 leading-relaxed max-w-2xl">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </main>
  );
}
